<script setup lang="ts">
import { ArrowDownTrayIcon, ArrowPathIcon } from "@heroicons/vue/20/solid";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { DateTime } from "luxon";
import { computed, ref, watch } from "vue";
import JsonCsv from "vue-json-csv";
import { useRoute } from "vue-router";
import { useLoading } from "../composables/useLoading";
import { useNotification } from "../composables/useNotification";
import { UserStore, useStores } from "../composables/useStores";
import { FirestoreService } from "../services/firestore";
import { Payment } from "../types/payment";
import { Store } from "../types/store";
const { setText, toggleLoading } = useLoading();
const { setError } = useNotification();
const { currentStore } = useStores();

const payments = ref<Payment[]>([]);
const refunds = ref<any[]>([]);
const init = async (store: UserStore, date: string) => {
  setText("Loading payments...");
  toggleLoading(true);
  try {
    const refundPath = `/users/${store.user}/merchants/${store.merchant}/stores/${store.store}/refund-days/${date}/refunds`;
    console.log({ refundPath });
    refunds.value = await FirestoreService.getCollection<any>(refundPath);
    console.log(refunds.value);
  } catch (e) {
    console.log(e);
    setError("failed to load payments please try again");
  } finally {
    toggleLoading(false);
  }
};
const route = useRoute();
const currentDate = ref(route.params.date as string);
watch(
  () => [currentDate.value, currentStore.value?.store],
  async ([dateVal, storeId]) => {
    if (!dateVal || !storeId) return;
    await init(currentStore.value!, dateVal);
  },
  { immediate: true },
);

const sortedRefunds = computed(() =>
  [...refunds.value.map((p) => ({ ...p, type: "refund" }))].sort((p1, p2) =>
    DateTime.fromISO(p1.date).toMillis() < DateTime.fromISO(p2.date).toMillis()
      ? 1
      : -1,
  ),
);

const zone = (store: Store) => {
  if (store.address?.state === "VIC") {
    return "Australia/Melbourne";
  }
  return "Australia/Brisbane";
};
// const dateUpdated = async (date: Date) => {
//   const x = DateTime.fromISO(date.toISOString())
//     .setZone(zone(currentStore.value!.data))
//     .toFormat("yyyy-MM-dd");
//   console.log({ date, x });
//   if (!currentStore.value) return;
//   await init(currentStore.value, x);
// };

const downloadable = computed(() => {
  if (!payments.value) return;
  if (payments.value.length === 0) return;
  if (!currentStore.value) return;
  return payments.value.map((payment) => {
    return {
      Date: DateTime.fromFormat(payment.date, "yyyy-MM-dd HH:mm:ss")
        .setZone(zone(currentStore.value!.data))
        .toFormat("yyyy-MM-dd HH:mm:ss"),
      RefundAmount: Number(payment.amount).toFixed(2),
      OriginalPaymentID:
        payment.merchantReference &&
        payment.merchantReference.startsWith("payopbl")
          ? `${payment.merchantReference} (Remote)`
          : payment.merchantReference ?? "N/A",
    };
  });
});
</script>
<template>
  <div class="px-4 pb-4 sm:pb-6 sm:px-6 lg:px-8 lg:pb-8">
    <div class="sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <div class="sm:flex sm:items-center mb-6">
          <div
            class="sm:flex-auto flex flex-row justify-start space-x-2 items-center"
          >
            <h1 class="text-[#111827] font-poppins font-semibold text-2xl">
              Initiated Refunds
            </h1>
            <arrow-path-icon
              class="w-6 h-6 text-black block cursor-pointer"
              @click="currentStore && init(currentStore, currentDate)"
            />
            <json-csv
              :data="downloadable"
              v-if="downloadable && currentStore"
              :name="`${currentStore.data.description}-payments-${currentDate}`"
            >
              <arrow-down-tray-icon
                class="w-6 h-6 text-black block cursor-pointer"
              />
            </json-csv>
          </div>
        </div>
        <div class="w-[200px] max-w-full my-4">
          <VueDatePicker
            v-model="currentDate"
            format="yyyy-MM-dd"
            model-type="yyyy-MM-dd"
            :enable-time-picker="false"
          />
        </div>
      </div>
    </div>
    <div class="p-4 bg-white rounded-[16px]">
      <div class="-mx-4 mt-8 sm:-mx-0">
        <table class="min-w-full" v-if="currentStore">
          <thead class="border-none">
            <tr class="border-none">
              <th
                scope="col"
                class="rounded-l-[16px] hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                Date
              </th>

              <th
                scope="col"
                class="hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                Refund Amount
              </th>
              <th
                scope="col"
                class="hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                Original Payment ID
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white">
            <tr v-for="payment in sortedRefunds" :key="payment.id">
              <td
                class="w-full max-w-0 py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:w-auto sm:max-w-none sm:pl-0"
              >
                {{
                  DateTime.fromFormat(payment.date, "yyyy-MM-dd HH:mm:ss")
                    .setZone(zone(currentStore.data))
                    .toFormat("yyyy-MM-dd HH:mm:ss")
                }}
                <dl class="font-normal lg:hidden">
                  <dt class="sr-only">Amount</dt>
                  <dd class="mt-1 truncate text-gray-700">
                    {{ payment.amount }}
                  </dd>
                </dl>
              </td>
              <td class="hidden px-3 py-4 text-sm text-gray-500 sm:table-cell">
                {{ Number(payment.amount).toFixed(2) }}
              </td>
              <td class="hidden px-3 py-4 text-sm text-gray-500 sm:table-cell">
                {{ payment.payment.merchantReference ?? "N/A" }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
